import { useEffect, useMemo, useState } from 'react'
import { onValue, ref } from 'firebase/database'

import { db, tref } from '../lib/firebase.js'
import { parsFor, rankTeams } from '../lib/scoring.js'
import PageNav from '../components/PageNav.jsx'
import CLUB from '../config/club.js'

// Public read-only tournament archive. Lists every closed tournament grouped
// by year with a podium per flight + contest winners. Older tournaments that
// were closed before results-snapshotting was added are computed on the fly
// from the stored team scores.
export default function HistoryApp() {
  const [tournaments, setTournaments] = useState({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const off = onValue(tref('tournaments'), (snap) => {
      setTournaments(snap.val() || {})
      setLoading(false)
    })
    return () => off()
  }, [])

  const byYear = useMemo(() => {
    const closed = Object.entries(tournaments)
      .filter(([, t]) => t?.info?.status === 'closed' && !t?.info?.isDemo)
      .map(([id, t]) => ({ id, ...t }))
    closed.sort((a, b) => yearOf(b) - yearOf(a) || (b.info?.closedAt || 0) - (a.info?.closedAt || 0))
    const map = new Map()
    for (const t of closed) {
      const y = yearOf(t)
      if (!map.has(y)) map.set(y, [])
      map.get(y).push(t)
    }
    return Array.from(map.entries()) // [[year, [tournaments]], ...]
  }, [tournaments])

  return (
    <div className="min-h-full relative" style={{ backgroundColor: CLUB.backgroundColor }}>
      <PageNav back />
      <header className="px-5 pt-14 pb-4 border-b" style={{ borderColor: `${CLUB.accentColor}30` }}>
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <div className="bg-white rounded-xl p-1.5 shrink-0" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.25)' }}>
            <img src={CLUB.logo} alt={CLUB.shortName} style={{ height: '52px', width: '52px', objectFit: 'contain', display: 'block' }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-widest" style={{ color: CLUB.accentColor }}>{CLUB.name}</p>
            <h1 className="text-2xl font-extrabold text-white">Tournament History</h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-5 space-y-8">
        {loading ? (
          <p className="text-center text-white/60 mt-12">Loading…</p>
        ) : byYear.length === 0 ? (
          <div
            className="rounded-2xl text-center py-12 px-6"
            style={{ backgroundColor: 'rgba(0,0,0,0.25)', border: `1px dashed ${CLUB.accentColor}40` }}>
            <div className="text-4xl mb-2">📜</div>
            <p className="text-white/80 font-bold">No tournaments closed yet.</p>
            <p className="text-white/50 text-sm mt-1">Close a tournament from the admin to record it here.</p>
          </div>
        ) : (
          byYear.map(([year, list]) => (
            <YearSection key={year} year={year} tournaments={list} />
          ))
        )}
      </main>
    </div>
  )
}

function yearOf(t) {
  const fromDate = (t?.info?.date || '').slice(0, 4)
  if (/^\d{4}$/.test(fromDate)) return Number(fromDate)
  const ts = t?.info?.closedAt || t?.info?.createdAt
  if (ts) return new Date(ts).getFullYear()
  return 0
}

function YearSection({ year, tournaments }) {
  return (
    <section>
      <div className="flex items-center gap-3 mb-4">
        <h2
          className="text-3xl font-extrabold"
          style={{ color: CLUB.accentColor, fontFamily: 'Cinzel, serif', letterSpacing: '0.05em' }}>
          {year}
        </h2>
        <div className="flex-1 h-px" style={{ backgroundColor: `${CLUB.accentColor}40` }} />
        <span className="text-xs uppercase tracking-widest text-white/50">
          {tournaments.length} tournament{tournaments.length === 1 ? '' : 's'}
        </span>
      </div>
      <div className="space-y-5">
        {tournaments.map((t) => <TournamentCard key={t.id} tournament={t} />)}
      </div>
    </section>
  )
}

function TournamentCard({ tournament }) {
  const info = tournament.info || {}
  const flights = Object.entries(tournament.flights || {})
    .filter(([id]) => !id.startsWith('_'))
  const styleLabel = info.playStyle === 'match' ? 'Match Play' : 'Stroke Play'

  return (
    <div
      className="rounded-2xl p-5"
      style={{ backgroundColor: 'rgba(0,0,0,0.30)', border: `1px solid ${CLUB.accentColor}40` }}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-xl font-extrabold text-white">
            🏆 {info.name}
          </h3>
          <p className="text-xs text-white/65 mt-0.5">
            {fmtDate(info.date) || '—'} · {info.format || '4-Ball Best Ball'} · {styleLabel}
          </p>
        </div>
      </div>

      <div className="mt-4 space-y-4">
        {flights.map(([flightId, f]) => (
          <FlightPodium
            key={flightId}
            flightId={flightId}
            flight={f}
            results={tournament.results?.flights?.[flightId]}
            pars={parsFor(tournament)}
          />
        ))}
        {flights.length === 0 ? (
          <p className="text-white/50 text-sm italic">No flights recorded.</p>
        ) : null}
      </div>

      <ContestStrip contests={tournament.contests} />
    </div>
  )
}

function FlightPodium({ flight, results, pars }) {
  // Use stored results if present, otherwise compute on-the-fly so legacy
  // closed tournaments still display.
  let first = results?.first
  let second = results?.second
  let third = results?.third
  if (!first && !second && !third) {
    const teams = Object.entries(flight.teams || {}).map(([id, v]) => ({ id, ...v }))
    if (teams.length > 0) {
      const ranked = rankTeams(teams, false, pars)
      first = ranked[0] && { teamId: ranked[0].id, teamName: ranked[0].info?.name }
      second = ranked[1] && { teamId: ranked[1].id, teamName: ranked[1].info?.name }
      third = ranked[2] && { teamId: ranked[2].id, teamName: ranked[2].info?.name }
    }
  }

  return (
    <div>
      <p className="text-xs uppercase tracking-widest font-bold mb-2" style={{ color: CLUB.accentColor }}>
        {flight.info?.name || 'Flight'}
      </p>
      <ul className="space-y-1 text-sm">
        <PodiumRow medal="🥇" entry={first} />
        <PodiumRow medal="🥈" entry={second} />
        <PodiumRow medal="🥉" entry={third} />
      </ul>
    </div>
  )
}

function PodiumRow({ medal, entry }) {
  if (!entry) return null
  return (
    <li className="flex items-center gap-2 text-white">
      <span className="text-lg" aria-hidden>{medal}</span>
      <span className="font-bold">{entry.teamName}</span>
    </li>
  )
}

function ContestStrip({ contests }) {
  if (!contests) return null
  const lines = []
  const kps = normalizeKps(contests)
  kps.forEach((k, i) => {
    if (!k?.winner) return
    lines.push({ key: `kp-${i}`, label: `KP H${k.hole ?? '?'}`, value: `${k.winner}${k.distance ? ` · ${k.distance}` : ''}` })
  })
  if (contests.longdrive?.winner) {
    lines.push({
      key: 'ld',
      label: `Long Drive H${contests.longdrive.hole ?? '?'}`,
      value: `${contests.longdrive.winner}${contests.longdrive.distance ? ` · ${contests.longdrive.distance}` : ''}`,
    })
  }
  if (lines.length === 0) return null
  return (
    <div className="mt-4 pt-3 border-t" style={{ borderColor: `${CLUB.accentColor}30` }}>
      <p className="text-xs uppercase tracking-widest font-bold mb-1.5" style={{ color: CLUB.accentColor }}>Contest Winners</p>
      <ul className="text-sm text-white/85 space-y-0.5">
        {lines.map((l) => (
          <li key={l.key}><span className="font-bold">{l.label}:</span> {l.value}</li>
        ))}
      </ul>
    </div>
  )
}

function normalizeKps(contests) {
  const raw = contests?.kps
  if (Array.isArray(raw)) return raw.filter(Boolean)
  if (raw && typeof raw === 'object') return Object.keys(raw).sort().map((k) => raw[k]).filter(Boolean)
  if (contests?.kp?.winner) return [contests.kp]
  return []
}

function fmtDate(d) {
  if (!d) return ''
  const dt = new Date(d)
  if (isNaN(dt)) return d
  return dt.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
}
